import React, { useState, useEffect, Fragment } from "react";
import {
  BrowserRouter as Router,
  Switch,
  Route,
  Link,
  useHistory,
  Redirect,
  useLocation,
  useParams,
} from "react-router-dom";
import "./index.scss";
import { useTranslation } from "react-i18next";
import "react-loading-skeleton/dist/skeleton.css";
import { useSelector } from "react-redux";
import Host from "../../Components/Host";
import { useGetUserDataQuery } from "../../services/login";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import PhoneIcon from "@mui/icons-material/Phone";
import HelmetComponent from "../../Components/HelmetComponent";

function ReturnsRefund() {
  const { userId, userToken, userEmail, userName, isMember, isPaymentMethod, languageCode, countryCode } = useSelector(
    (state) => state.auth
  );
  const history = useHistory();
  const location = useLocation();
  const { t } = useTranslation();
  const [getSearchId, setSearchId] = useState("");

  useEffect(() => {
    window.scrollTo({
      top: 0,
      left: 0,
      // behavior: "smooth"
    });
  }, []);

  const { data: userData } = useGetUserDataQuery(
    { userId: userId, userToken: userToken, countryCode: countryCode },
    { count: 1 },
    { refetchOnMountOrArgChange: true }
  );

  const myElementRef = React.useRef(null);
  const myElementRefs = React.useRef(null);
  const myContact = React.useRef(null);

  const myPayment = React.useRef(null);
  const myBilling = React.useRef(null);

  useEffect(() => {
    const handleHashChange = () => {
      const hash = window.location.hash.substring(1);
      if (hash === "returns") {
        myElementRef.current.scrollIntoView({ behavior: "smooth" });
      }
      if (hash === "shipping") {
        myElementRefs.current.scrollIntoView({ behavior: "smooth" });
      }
      if (hash === "contact") {
        myContact.current.scrollIntoView({ behavior: "smooth" });
      }
      if (hash === "billing") {
        myBilling.current.scrollIntoView({ behavior: "smooth" });
      }
      if (hash === "payment") {
        myPayment.current.scrollIntoView({ behavior: "smooth" });
      }
    };
    window.addEventListener("hashchange", handleHashChange);
    handleHashChange();
    return () => {
      window.removeEventListener("hashchange", handleHashChange);
    };
  }, [window, window.location.hash]);

  return (
    <>
      <HelmetComponent title="Return refunds" />
      <div className="statisc-page">
        <div className="container static-container">
          <div className="row static-row">
            <div className="col-md-10">
              <div
                className="static-container-card"
                style={{
                  textAlign: "left",
                  padding: "20px",
                  background: "#fcfcfc",
                  border: "1px solid #eee",
                  borderRadius: "7px",
                }}
              >
                <h1 style={{ fontSize: "1.4rem" }}>Return & Refund Policy</h1>

                <p>
                  At <strong>luxuracart.com</strong>, we want you to enjoy a smooth, worry-free shopping experience.
                  While we work hard to bring you authentic, well-curated products, we also understand that sometimes
                  things do not go as planned.
                </p>

                <p>
                  This policy explains when and how returns, refunds, and cancellations work, and also sets clear
                  boundaries to protect both you as a customer and us as a business from misuse or fraud.
                </p>

                <h4>1. Eligibility for Returns</h4>
                <p>You may request a return if you receive a wrong, damaged, defective, or incomplete product.</p>

                <h4>1.1 Return Window</h4>
                <ul>
                  <li>You can raise a return request within 7 days of receiving your order.</li>
                  <li>
                    For items that arrive damaged, defective, or incomplete, please contact us within 2 days of delivery
                    and share clear photos or videos so that we can investigate and help you faster.
                  </li>
                </ul>

                <h4>1.2 Condition of the Product</h4>
                <p>To be eligible for a return, the product must:</p>
                <ul>
                  <li>Be in new, unused, and resalable condition.</li>
                  <li>
                    Have all original packaging, including the brand box, labels, barcodes, user manuals, warranty
                    cards, and accessories.
                  </li>
                  <li>Not be damaged, altered, washed, worn, resized, stained, or used by the customer.</li>
                  <li>No tampering or modification on serial numbers, IMEI, barcodes, or warranty stickers.</li>
                </ul>

                <p>
                  If the returned product does not meet the above conditions, your return may be rejected or only
                  partially refunded at our discretion.
                </p>

                <h4>2. Non-returnable / Non-refundable Products</h4>
                <p>
                  Certain products are sensitive from a hygiene, safety, or fraud standpoint. These items are not
                  eligible for return, replacement, or refund, except in rare cases where they are received damaged or
                  incorrect and reported within the stipulated time.
                </p>

                <h4>2.1 Intimate and Hygiene-sensitive Items</h4>
                <ul>
                  <li>Undergarments, innerwear, shapewear, lingerie</li>
                  <li>Swimwear and beachwear</li>
                  <li>Socks, stockings, and similar apparel</li>
                  <li>Reusable sanitary or intimate products</li>
                </ul>

                <h4>2.2 Beauty, Personal Care & Health</h4>
                <ul>
                  <li>Skincare, haircare, bath and body products</li>
                  <li>Perfumes, deodorants, body mists</li>
                  <li>Cosmetics and makeup</li>
                  <li>Oral care products</li>
                  <li>Health and wellness sealed products</li>
                </ul>

                <h4>2.3 Food, Nutrition & Consumables</h4>
                <ul>
                  <li>Groceries, snacks, beverages</li>
                  <li>Vitamins, supplements, nutraceuticals</li>
                  <li>Perishable / temperature-sensitive items</li>
                  <li>Products with short shelf life</li>
                </ul>

                <h4>2.4 Medical, Safety & Hygiene Products</h4>
                <ul>
                  <li>Face masks and respirators</li>
                  <li>Medical devices with opened seals</li>
                  <li>Personal hygiene gear</li>
                </ul>

                <h4>2.5 Electronics and Electrical Products</h4>
                <p>Not eligible if:</p>
                <ul>
                  <li>Activated, paired, logged into, installed, or registered</li>
                  <li>Missing or tampered serial numbers, IMEI, or barcodes</li>
                </ul>

                <h4>2.6 Opened / Used / Altered Products</h4>
                <ul>
                  <li>Used, washed, worn, altered, or damaged by customer</li>
                  <li>Missing packaging, accessories, or freebies</li>
                  <li>Stains, odors, or visible signs of usage</li>
                </ul>

                <h4>2.7 Refurbished, Pre-owned & Clearance Items</h4>
                <p>
                  Sold as <strong>final sale</strong>, hence non-returnable unless explicitly mentioned otherwise.
                </p>

                <h4>2.8 Digital & Subscription Products</h4>
                <ul>
                  <li>Digital downloads, software, e-books, license keys</li>
                  <li>Subscriptions, memberships, gift cards</li>
                </ul>

                <h4>2.9 Customized Products</h4>
                <ul>
                  <li>Customized, engraved, monogrammed, or made-to-order products</li>
                </ul>

                <h4>2.10 Bundles, Kits & Freebies</h4>
                <ul>
                  <li>Must be returned as a complete set, if returnable</li>
                  <li>Free gifts are not eligible for return</li>
                </ul>

                <h4>2.11 Returns Not Matching Approved Conditions</h4>
                <p>We reserve the right to refuse such returns.</p>

                <h4>3. Anti-Fraud and Policy Misuse Protection</h4>
                <ul>
                  <li>All returns are inspected.</li>
                  <li>Mismatch in serial numbers leads to rejection.</li>
                  <li>Unboxing video may be required.</li>
                  <li>Excessive or abusive behavior may result in blocked services.</li>
                </ul>

                <h4>4. Refund Process</h4>
                <h4>4.1 Standard Refunds</h4>
                <ul>
                  <li>Refunds issued as store credit or to original payment method.</li>
                  <li>Processed within 7–10 business days after QC check.</li>
                </ul>

                <h4>4.2 Refunds for Damaged / Incorrect Products</h4>
                <ul>
                  <li>Full refund or replacement after verification.</li>
                  <li>Taxes and duties refunded, if applicable.</li>
                </ul>

                <h4>5. Return Process</h4>
                <ol>
                  <li>
                    Contact us via Live Chat, email: <strong>cs@luxuracart.com</strong> or call{" "}
                    <strong>+91-9653498993</strong>
                  </li>
                  <li>Share photos/videos of the issue.</li>
                  <li>Team will review and guide next steps.</li>
                  <li>Pickup arranged, or customer may ship back if required.</li>
                  <li>Item must be securely packed with all contents.</li>
                </ol>

                <h4>6. Sale & Promotional Items</h4>
                <p>Generally non-returnable unless defective.</p>

                <h4>7. Exceptions</h4>
                <ul>
                  <li>Items purchased by mistake without defect</li>
                  <li>Used or altered products</li>
                  <li>Non-returnable categories listed above</li>
                </ul>

                <h4>8. Cancellation Policy</h4>
                <ul>
                  <li>Orders can be cancelled before shipment.</li>
                  <li>If already shipped, refuse delivery for refund.</li>
                </ul>

                <h4>9. Contact Us</h4>
                <p>For any questions:</p>

                <div style={{ lineHeight: 2 }}>
                  <p>
                    {" "}
                    <strong>Luxura Cart Ecommerce Private Limited</strong>
                  </p>
                  <p>
                    <strong>India Operations and Support</strong>
                    <div
                      dangerouslySetInnerHTML={{
                        __html: Host?.address,
                      }}
                    ></div>
                    <MailOutlineIcon style={{ fontSize: "1.1rem", color: "#444" }} />
                    &nbsp; {Host?.email} <br />
                    <PhoneIcon style={{ fontSize: "1.1rem", color: "#444" }} />
                    &nbsp; {Host?.phone}
                  </p>

                  <p>
                    <strong>Global Operations and Support</strong>
                    <div
                      dangerouslySetInnerHTML={{
                        __html: Host?.addressGlobal,
                      }}
                    ></div>
                    <MailOutlineIcon style={{ fontSize: "1.1rem", color: "#444" }} />
                    &nbsp; {Host?.emailGlobal} <br />
                    <PhoneIcon style={{ fontSize: "1.1rem", color: "#444" }} />
                    &nbsp; {Host?.phoneGlobal}
                  </p>
                </div>

                <ul>
                  <li>Live Chat on luxuracart.com</li>
                </ul>

                <p>We are here to help you, while keeping the platform safe and fair for all genuine customers.</p>

                <p>
                  <strong>
                    Yours Truly,
                    <br />
                    Luxura Cart
                  </strong>
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default ReturnsRefund;
